import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [platform, setPlatform] = useState({ product_name: "ChitChat Sales CRM" });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      const u = data.user;
      setUser(u);
      setTenant(data.tenant);

      // Every user has a personal operational CRM scope. Reps can never switch
      // away from themselves; owners/managers keep a deliberately selected team
      // member across refreshes until they switch back to My CRM.
      if (u?.id) {
        if (u.role === "sales_rep" || !localStorage.getItem("cc_crm_scope")) {
          localStorage.setItem("cc_crm_scope", u.id);
        }
      }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/platform/config").then((r) => setPlatform(r.data)).catch(() => {});
    if (localStorage.getItem("cc_token")) refresh();
    else {
      setUser(false);
      setLoading(false);
    }
  }, [refresh]);

  const login = async (email, password, remember) => {
    const device_token = localStorage.getItem("cc_device") || undefined;
    const { data } = await api.post("/auth/login", { email, password, remember, device_token });
    if (data.twofa_required) return data; // caller drives the 2FA step
    localStorage.setItem("cc_token", data.token);
    await refresh();
    return data;
  };

  const completeLogin = async (token, deviceToken) => {
    localStorage.setItem("cc_token", token);
    if (deviceToken) localStorage.setItem("cc_device", deviceToken);
    await refresh();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("cc_token");
    localStorage.removeItem("cc_crm_scope");
    setUser(false);
    setTenant(null);
    window.location.href = "/login";
  };

  const can = (...roles) => user && roles.includes(user.role);
  const isAdmin = !!user && ["tenant_owner", "platform_owner"].includes(user.role);

  return (
    <AuthCtx.Provider value={{ user, tenant, platform, loading, login, completeLogin, logout, refresh, setTenant, can, isAdmin }}>
      {children}
    </AuthCtx.Provider>
  );
}
